import type { ReactNode } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  CollapsibleSection,
  Select,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Tooltip } from '@/components/ui/Tooltip';
import type {
  NotificationEventInfo,
  NotificationFrequency,
  NotificationModule,
  NotificationScope,
  NotificationSubmodule,
} from '@/lib/types';
import {
  FREQUENCY_OPTIONS,
  SCOPE_OPTIONS,
  SOURCE_LABELS,
  groupState,
  lockedReason,
  type ChannelField,
  type EventView,
  type GroupState,
} from './model';

export interface ModuleCardProps {
  mod: NotificationModule;
  views: ReadonlyMap<string, EventView>;
  /** Events the search / chip filter leaves on screen. */
  isVisible: (ev: NotificationEventInfo) => boolean;
  collapsed: boolean;
  onToggle: () => void;
  disabled: boolean;
  canReset: boolean;
  onReset: () => void;
  /** Every event in the module is no access: "No access" badge on the title. */
  noAccess: boolean;
  /** Disabled reason on no-access controls ("Your permission set does not include …"). */
  noAccessReason: string;
  onEventChannel: (ev: NotificationEventInfo, field: ChannelField, value: boolean) => void;
  onEventFrequency: (ev: NotificationEventInfo, value: NotificationFrequency) => void;
  onEventScope: (ev: NotificationEventInfo, value: NotificationScope) => void;
  onGroupChannel: (sub: NotificationSubmodule | null, field: ChannelField, value: boolean) => void;
}

const CHANNELS: readonly { field: ChannelField; label: string }[] = [
  { field: 'emailEnabled', label: 'Email' },
  { field: 'inboxEnabled', label: 'In-app' },
];

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0 text-fg-subtle"
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/** A checkbox whose disabled reason, when there is one, sits on a wrapper tooltip. */
function Reasoned({ reason, children }: { reason: string | null; children: ReactNode }) {
  if (!reason) return <>{children}</>;
  return (
    <Tooltip label={reason} describes>
      <span className="inline-flex">{children}</span>
    </Tooltip>
  );
}

function GroupSwitch({
  state,
  group,
  label,
  visibleLabel,
  reason,
  noAccessReason,
  disabled,
  onChange,
}: {
  state: GroupState;
  group: string;
  label: string;
  visibleLabel: boolean;
  reason: string;
  noAccessReason: string;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  const locked = state === 'locked';
  const noAccess = state === 'no_access';
  return (
    <Reasoned reason={locked ? reason : noAccess ? noAccessReason : null}>
      <Checkbox
        ref={(el) => {
          if (el) el.indeterminate = state === 'mixed';
        }}
        label={
          visibleLabel ? (
            <>
              <span className="sr-only">{`${group} `}</span>
              {label}
            </>
          ) : (
            <span className="sr-only">{`${group} ${label}`}</span>
          )
        }
        checked={state === 'on' || locked}
        aria-checked={state === 'mixed' ? 'mixed' : undefined}
        disabled={disabled || locked || noAccess}
        onChange={() => onChange(state !== 'on')}
      />
    </Reasoned>
  );
}

function EventLabel({ ev, view, moduleKey }: { ev: NotificationEventInfo; view: EventView; moduleKey: string }) {
  const sourceLabel = view.source ? SOURCE_LABELS[view.source] : null;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
      {view.locked && (
        <Tooltip label={lockedReason(moduleKey)}>
          <span className="inline-flex" aria-label={lockedReason(moduleKey)} role="img">
            <LockIcon />
          </span>
        </Tooltip>
      )}
      <span className="min-w-0 text-sm text-fg">{ev.label}</span>
      {sourceLabel && <span className="text-2xs text-fg-subtle">{sourceLabel}</span>}
      {view.source === 'user' && <span className="text-2xs text-fg-subtle">Changed</span>}
    </span>
  );
}

function EventControls({
  ev,
  view,
  moduleKey,
  disabled,
  layout,
  props,
}: {
  ev: NotificationEventInfo;
  view: EventView;
  moduleKey: string;
  disabled: boolean;
  layout: 'row' | 'card';
  props: ModuleCardProps;
}) {
  const reason = view.locked ? lockedReason(moduleKey) : view.noAccess ? props.noAccessReason : null;
  const fixed = view.locked || view.noAccess;
  const channel = (field: ChannelField, label: string) => (
    <Reasoned reason={reason}>
      <Checkbox
        label={layout === 'card' ? label : <span className="sr-only">{`${label}: ${ev.label}`}</span>}
        checked={field === 'emailEnabled' ? view.email : view.inbox}
        disabled={disabled || fixed}
        onChange={(event) => props.onEventChannel(ev, field, event.target.checked)}
      />
    </Reasoned>
  );
  const frequency = (
    <Reasoned reason={reason}>
      <Select
        aria-label={`Frequency: ${ev.label}`}
        value={view.frequency}
        disabled={disabled || fixed}
        onChange={(event) => props.onEventFrequency(ev, event.target.value as NotificationFrequency)}
      >
        {FREQUENCY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </Reasoned>
  );
  const scope = ev.clientEvent ? (
    <Reasoned reason={view.noAccess ? props.noAccessReason : null}>
      <Select
        aria-label={`Scope: ${ev.label}`}
        value={view.scope}
        disabled={disabled || view.noAccess}
        onChange={(event) => props.onEventScope(ev, event.target.value as NotificationScope)}
      >
        {SCOPE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </Reasoned>
  ) : null;

  if (layout === 'row') {
    return (
      <>
        <TD className="w-20">{channel('emailEnabled', 'Email')}</TD>
        <TD className="w-20">{channel('inboxEnabled', 'In-app')}</TD>
        <TD className="w-44">{frequency}</TD>
        <TD className="w-40">{scope}</TD>
      </>
    );
  }
  return (
    <div className="mt-2 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        {channel('emailEnabled', 'Email')}
        {channel('inboxEnabled', 'In-app')}
      </div>
      <div className="grid grid-cols-1 gap-2 min-[380px]:grid-cols-2">
        {frequency}
        {scope}
      </div>
    </div>
  );
}

/** One catalogue module: module switches, submodule switches, event rows. */
export function ModuleCard(props: ModuleCardProps) {
  const {
    mod,
    views,
    isVisible,
    collapsed,
    onToggle,
    disabled,
    canReset,
    onReset,
    onGroupChannel,
    noAccess,
    noAccessReason,
  } = props;

  const viewsOf = (events: readonly NotificationEventInfo[]) =>
    events.map((ev) => views.get(ev.key)).filter((view): view is EventView => view !== undefined);
  const allEvents = mod.submodules.flatMap((sub) => sub.events);
  const moduleViews = viewsOf(allEvents);
  const reason = lockedReason(mod.key);
  const subs = mod.submodules
    .map((sub) => ({ sub, events: sub.events.filter(isVisible) }))
    .filter((entry) => entry.events.length > 0);
  const changed = moduleViews.filter((view) => view.source === 'user').length;

  return (
    <CollapsibleSection
      id={`notifications-${mod.key}`}
      title={
        noAccess ? (
          <span className="inline-flex items-center gap-2">
            {mod.label}
            <Badge tone="neutral">No access</Badge>
          </span>
        ) : (
          mod.label
        )
      }
      titleText={mod.label}
      collapsed={collapsed}
      onToggle={onToggle}
      summary={`${allEvents.length} ${allEvents.length === 1 ? 'event' : 'events'}${changed > 0 ? ` · ${changed} changed` : ''}`}
      actions={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {CHANNELS.map((channel) => (
            <GroupSwitch
              key={channel.field}
              state={groupState(moduleViews, channel.field)}
              group={mod.label}
              label={channel.label}
              visibleLabel
              reason={reason}
              noAccessReason={noAccessReason}
              disabled={disabled}
              onChange={(value) => onGroupChannel(null, channel.field, value)}
            />
          ))}
          <Reasoned reason={canReset ? null : 'Already on team defaults'}>
            <Button size="sm" variant="ghost" disabled={disabled || !canReset} onClick={onReset}>
              Reset to team defaults
            </Button>
          </Reasoned>
        </div>
      }
    >
      {/* Desktop: one table, a header row per submodule. */}
      <div className="max-md:hidden">
        <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
          <Table>
            <THead>
              <TR>
                <TH>Event</TH>
                <TH className="w-20">Email</TH>
                <TH className="w-20">In-app</TH>
                <TH className="w-44">Frequency</TH>
                <TH className="w-40">Scope</TH>
              </TR>
            </THead>
            <TBody>
              {subs.map(({ sub, events }) => {
                const subViews = viewsOf(sub.events);
                return [
                  <TR key={`sub-${sub.key}`} className="bg-surface-sunken">
                    <TD className="text-xs font-semibold text-fg-secondary">{sub.label}</TD>
                    {CHANNELS.map((channel) => (
                      <TD key={channel.field} className="w-20">
                        <GroupSwitch
                          state={groupState(subViews, channel.field)}
                          group={sub.label}
                          label={channel.label}
                          visibleLabel={false}
                          reason={reason}
                          noAccessReason={noAccessReason}
                          disabled={disabled}
                          onChange={(value) => onGroupChannel(sub, channel.field, value)}
                        />
                      </TD>
                    ))}
                    <TD />
                    <TD />
                  </TR>,
                  ...events.map((ev) => {
                    const view = views.get(ev.key);
                    if (!view) return null;
                    return (
                      <TR key={ev.key}>
                        <TD>
                          <span className="pl-3">
                            <EventLabel ev={ev} view={view} moduleKey={mod.key} />
                          </span>
                        </TD>
                        <EventControls
                          ev={ev}
                          view={view}
                          moduleKey={mod.key}
                          disabled={disabled}
                          layout="row"
                          props={props}
                        />
                      </TR>
                    );
                  }),
                ];
              })}
            </TBody>
          </Table>
        </TableWrap>
      </div>

      {/* Below md: stacked cards, same controls. */}
      <div className="md:hidden">
        {subs.map(({ sub, events }) => {
          const subViews = viewsOf(sub.events);
          return (
            <section key={sub.key} aria-label={sub.label} className="border-b border-border last:border-b-0">
              <div className="flex flex-wrap items-center justify-between gap-2 bg-surface-sunken px-3 py-2">
                <span className="text-xs font-semibold text-fg-secondary">{sub.label}</span>
                <div className="flex items-center gap-4">
                  {CHANNELS.map((channel) => (
                    <GroupSwitch
                      key={channel.field}
                      state={groupState(subViews, channel.field)}
                      group={sub.label}
                      label={channel.label}
                      visibleLabel
                      reason={reason}
                      noAccessReason={noAccessReason}
                      disabled={disabled}
                      onChange={(value) => onGroupChannel(sub, channel.field, value)}
                    />
                  ))}
                </div>
              </div>
              <ul className="divide-y divide-border">
                {events.map((ev) => {
                  const view = views.get(ev.key);
                  if (!view) return null;
                  return (
                    <li key={ev.key} className="px-3 py-3">
                      <EventLabel ev={ev} view={view} moduleKey={mod.key} />
                      <EventControls
                        ev={ev}
                        view={view}
                        moduleKey={mod.key}
                        disabled={disabled}
                        layout="card"
                        props={props}
                      />
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}
